import React, { createContext, useCallback, useContext, useState } from "react";

// ── Consentimiento de cookies (RGPD / LSSI-CE) ──────────────────────────────
//
// Se guarda en localStorage junto a un número de versión: si el día de mañana
// cambian las categorías de cookies (por ejemplo, se añade Google Analytics),
// basta con subir COOKIE_CONSENT_VERSION para que a todo el mundo se le
// vuelva a preguntar. Es justo lo que promete el punto 7 de la Política de
// Cookies: "en caso de cambios significativos, se informará a los usuarios".
//
// Cualquier script que no sea estrictamente necesario (Google Analytics, un
// píxel de redes sociales…) debe comprobar `preferences.analytics` /
// `preferences.marketing` antes de cargarse, y no hacerlo si no está en
// `true`. Hoy la web no incluye ninguno de esos scripts —solo gestiona el
// aviso legal—, pero este es el punto de control para cuando se añadan.
const STORAGE_KEY = "edh_cookie_consent";
const COOKIE_CONSENT_VERSION = 1;

const DEFAULT_PREFERENCES = {
  necessary: true, // Nunca se pueden desactivar.
  analytics: false,
  personalization: false,
  marketing: false,
};

function readStoredConsent() {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (parsed?.version !== COOKIE_CONSENT_VERSION || !parsed?.preferences) return null;
    return parsed;
  } catch {
    return null;
  }
}

const CookieConsentContext = createContext(null);

export function CookieConsentProvider({ children }) {
  const [stored, setStored] = useState(readStoredConsent);
  const [preferencesOpen, setPreferencesOpen] = useState(false);

  const persist = useCallback((preferences) => {
    const record = {
      version: COOKIE_CONSENT_VERSION,
      preferences,
      decidedAt: new Date().toISOString(),
    };
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(record));
    } catch {
      // Navegación privada o cuota de almacenamiento llena: el consentimiento
      // vive solo en memoria durante esta sesión. No es motivo para bloquear
      // al usuario ni para reintentar.
    }
    setStored(record);
    setPreferencesOpen(false);
  }, []);

  const acceptAll = useCallback(
    () => persist({ necessary: true, analytics: true, personalization: true, marketing: true }),
    [persist]
  );

  const rejectNonEssential = useCallback(
    () => persist({ ...DEFAULT_PREFERENCES }),
    [persist]
  );

  const savePreferences = useCallback(
    (preferences) => persist({ ...DEFAULT_PREFERENCES, ...preferences, necessary: true }),
    [persist]
  );

  const openPreferences = useCallback(() => setPreferencesOpen(true), []);
  const closePreferences = useCallback(() => setPreferencesOpen(false), []);

  const value = {
    preferences: stored?.preferences ?? DEFAULT_PREFERENCES,
    hasResponded: !!stored,
    // Sin decisión guardada (o con una de una versión ya obsoleta), el aviso
    // simple se muestra en cuanto se carga la web.
    bannerVisible: !stored,
    preferencesOpen,
    acceptAll,
    rejectNonEssential,
    savePreferences,
    openPreferences,
    closePreferences,
  };

  return (
    <CookieConsentContext.Provider value={value}>
      {children}
    </CookieConsentContext.Provider>
  );
}

export function useCookieConsent() {
  const ctx = useContext(CookieConsentContext);
  if (!ctx) {
    throw new Error("useCookieConsent debe usarse dentro de <CookieConsentProvider>");
  }
  return ctx;
}
